import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { Section } from '@shared/ui/organisms/layout/Layout';
import TextField from '@shared/ui/atoms/TextField';
import { Permission } from '@shared/models/user.models';
import { updatePermissions } from '@features/settings/services/ManagementService';
import { selectCurrentUser } from '@shared/store/auth.duck';
import SelectableContentCard from './components/SelectableContentCard';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import {
  canChangeUsersDefaultOffice,
  canChangeUsersRole,
  isCompanyAdminUser,
  isDepartmentAdmin,
  isOfficeAdminUser,
  isTeamAdminUser
} from '@shared/utils/AccessControl';
import Colours from '@shared/ui/atoms/Colours';
import SelectLocationSection from "./components/SelectLocationSection";
import {
  fetchEditUser,
  resetEditUser,
  selectEdittingUser,
  selectHasEditUserErrored,
  selectIsEditUserLoading,
  updateUser
} from '@features/settings/store/editUser.duck';
import UsersLineReports from './components/UsersLineReports';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { AdditionalResponsibilitiesSection } from "./components/AdditionalResponsibilitiesSection";
import { UserHolidayAllowanceSection } from "./components/UserHolidayAllowanceSection";
import { selectAllTeams } from "@shared/store/config.duck";
import { app } from "@microsoft/teams-js";
import { teamsTabs, uiUrl } from "@shared/services/EnvironmentVariables";
import { deepLinkTo, isInTeams, teamsReturnLink } from "@shared/utils/TeamsUtils";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChangeUsersTeamDropDownComponent } from "./edit-user-page/ChangeUsersTeamDropDownComponent";
import AccessRequired from "./components/AccessRequired";
import { UserAccessPermittedControl } from "./edit-user-page/UserAccessPermittedControl";
import { ChangeUsersDepartmentDropDownComponent } from "./edit-user-page/ChangeUsersDepartmentDropDownComponent";
import { UserWorkGroupsSection } from "./edit-user-page/UserWorkGroupsSection";
import { selectAllDepartments } from "@features/whereabouts/pages/departments/data/departments.duck";
import { MountedContentCard } from '@shared/ui/atoms/MountedContentCard';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { BirthdaySection } from "./components/BirthdaySection";
import { UserMacAddressSection } from "./components/UserMacAddressSection";
import { UpdateUserField, updateUserField } from '@features/settings/services/UserService';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { ConfigurationSection } from './StyleComponents';
import { SectionSaveButton } from './company-management-page-v2/components/SectionSaveButton';
import {
  SettingsMenuItem,
  SettingsMessageContainer,
  SettingsPageLayout,
  SettingsRibbon,
  SettingsSidebarLayout,
  useSettingsPanel
} from './components/SettingsSidebarLayout';

type EditUserPanel = 'profile' | 'team' | 'line-reports' | 'office' | 'access' | 'leave' | 'responsibilities' | 'danger-zone';

const TITLE = 'settings-page.edit-user.title';

export default function EditUserPage() {
  const params: any = useParams();
  const dispatch = useDispatch();
  const editingUser = useSelector(selectEdittingUser);
  const isLoading = useSelector(selectIsEditUserLoading);
  const teams = useSelector(selectAllTeams);
  const currentUser = useSelector(selectCurrentUser);
  const hasEditUserErrored = useSelector(selectHasEditUserErrored);
  const departments = useSelector(selectAllDepartments);
  const offices = useSelector(selectAllOfficeEntities);
  const {t} = useTranslation();
  const editingUserId = params.userId;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [permission, setPermission] = useState(Permission.Basic);
  const [subEntityId, setSubEntityId] = useState<string | null>(null);

  const company = currentUser?.companyEntity;
  const showOffice = offices.length > 0 || !!company?.automaticOfficeCheckinEnabled;
  const showLeave = !!company?.enableHolidayBooking;
  const showLineReports = !!company?.lineManagementEnabled;

  const menuItems = useMemo<SettingsMenuItem<EditUserPanel>[]>(() => {
    const items: (SettingsMenuItem<EditUserPanel> & { visible: boolean })[] = [
      { view: 'profile', icon: IconTypes.Profile, label: 'settings-page.edit-user.profile-tab', visible: true },
      { view: 'team', icon: IconTypes.Users, label: 'settings-page.edit-user.team-tab', visible: true },
      { view: 'line-reports', icon: IconTypes.Share, label: 'settings.line-reports', visible: showLineReports },
      { view: 'office', icon: IconTypes.Office, label: 'settings-page.edit-user.office-tab', visible: showOffice },
      { view: 'access', icon: IconTypes.Padlock, label: 'settings-page.edit-user.access-tab', visible: true },
      { view: 'leave', icon: IconTypes.Holiday, label: 'settings-page.edit-user.leave-tab', visible: showLeave },
      { view: 'responsibilities', icon: IconTypes.HardHat, label: 'settings-page.edit-user.responsibilities-tab', visible: true },
      { view: 'danger-zone', icon: IconTypes.Info, label: 'company-settings-page.danger-zone', visible: true },
    ];
    return items.filter(item => item.visible);
  }, [showOffice, showLeave, showLineReports]);

  const [activePanel, setActivePanel] = useSettingsPanel(menuItems.map(item => item.view));

  const canLoadUserToEdit = useMemo(() => {
    return isCompanyAdminUser(currentUser) || isDepartmentAdmin(currentUser) || isOfficeAdminUser(currentUser) || isTeamAdminUser(currentUser);
  }, [currentUser]);

  useEffect(() => {
    if (isInTeams()) {
      app.getContext().then((context: app.Context) => {
        setSubEntityId(context.page.subPageId ?? null);
      })
    }

    return () => {
      dispatch(resetEditUser());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!editingUser && editingUserId && canLoadUserToEdit) {
      dispatch(fetchEditUser(editingUserId));
    }
  }, [editingUser, editingUserId, canLoadUserToEdit, dispatch]);

  useEffect(() => {
    if (editingUser) {
      setFirstName(editingUser.firstName);
      setLastName(editingUser.lastName);
      setJobTitle(editingUser?.jobTitle ?? '')
      setPermission(editingUser.role);
    }
  }, [editingUser]);

  // Saves the Profile panel's text fields. Every other control on the page
  // saves as soon as it changes, so the section save button only confirms.
  const saveProfile = async () => {
    if (!editingUser) return;

    const namesChanged = firstName !== editingUser.firstName || lastName !== editingUser.lastName;
    const jobTitleChanged = jobTitle !== (editingUser.jobTitle ?? '');

    if (jobTitle && jobTitleChanged) {
      await updateUserField(editingUser.userId, UpdateUserField.jobTitle, jobTitle);
    }
    if (namesChanged || jobTitleChanged) {
      // holidayAllowance is sent as stored: the API writes it back, so falling
      // back to the company allowance here would pin it on the user.
      await dispatch(updateUser({
        ...editingUser,
        firstName: firstName,
        lastName: lastName,
      })).unwrap();
    }
  }

  const onLocationChange = async () => {
    dispatch(fetchEditUser(params.userId));
  }

  const openDeleteUserDialog = () => {
    if (!editingUser || !isCompanyAdminUser(currentUser)) return;
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.DeleteUserDialog,
      payload: {userInfo: editingUser},
    }));
  }

  const onBackButtonPressed = () => {
    const returnLink = teamsReturnLink(subEntityId);
    if (returnLink) {
      // Opened from another Teams tab (e.g. the whereabouts hover box), so go back to that tab
      deepLinkTo(returnLink.tabId, returnLink.url);
    } else if (subEntityId) {
      // Deep link back to settings page to reset subEntityId
      deepLinkTo(teamsTabs.settings, `${uiUrl}/manage`);
    } else {
      // Else just go back
      window.history.back();
    }
  }

  if (!canLoadUserToEdit) {
    return (
      <SettingsPageLayout>
        <SettingsRibbon title={TITLE} onBack={onBackButtonPressed} />
        <SettingsMessageContainer>
          <AccessRequired visible={true} />
        </SettingsMessageContainer>
      </SettingsPageLayout>
    );
  }

  if (hasEditUserErrored) {
    return (
      <SettingsPageLayout>
        <SettingsRibbon title={TITLE} onBack={onBackButtonPressed} />
        <SettingsMessageContainer>
          <Center>
            <MountedContentCard>
              <BodyRegular weight={600}>Failed to load</BodyRegular>
              <BodyRegular style={{marginBottom: 32}}>Please try again or is the problem persists contact <a href="mailto:support@team-today.com">support@team-today.com</a>.</BodyRegular>
              <PrimaryButton text={"Try again"} fullWidth={true} click={() => window.location.reload()} />
            </MountedContentCard>
          </Center>
        </SettingsMessageContainer>
      </SettingsPageLayout>
    );
  }

  if (!editingUser || !currentUser) {
    return (
      <SettingsPageLayout>
        <SettingsRibbon title={TITLE} onBack={onBackButtonPressed} />
        <SettingsMessageContainer>
          <Center>
            <LoadingSpinner hideBorder={true} />
          </Center>
        </SettingsMessageContainer>
      </SettingsPageLayout>
    );
  }

  const panelTitle = (label: string) => <HeadlineSmall style={{marginBottom: 16}}>{t(label)}</HeadlineSmall>;

  return (
    <SettingsPageLayout>
      {isLoading && <LoadingSpinner fullScreen={true} />}
      <SettingsRibbon title={TITLE}
                      onBack={onBackButtonPressed}
                      subtitle={<RibbonSubtitle>
                        <BodyRegular weight={600}>{editingUser.firstName} {editingUser.lastName}</BodyRegular>
                        <BodyRegular colour={Colours.darkGrey}>{editingUser.email}</BodyRegular>
                      </RibbonSubtitle>} />

      <SettingsSidebarLayout menuItems={menuItems}
                             activeView={activePanel}
                             onViewChange={setActivePanel}>

        {activePanel === 'profile' && <>
          {panelTitle('settings-page.edit-user.profile-tab')}
          <TextField dataTest="editUserPage-firstName-input" label="textinput.firstname"
                     fullWidth={true}
                     onChange={(val) => setFirstName(val)} value={firstName}/>
          <TextField dataTest="editUserPage-lastName-input" label="textinput.lastname"
                     fullWidth={true}
                     onChange={(val) => setLastName(val)} value={lastName}/>
          <TextField label="textinput.jobTitle"
                     fullWidth={true}
                     onChange={(val) => setJobTitle(val)}
                     value={jobTitle} />
          <ConfigurationSection>
            <BodyLarge colour={Colours.blue} style={{marginBottom: 6}}>{t('settings-page.edit-user.email-address')}</BodyLarge>
            <BodyRegular>{editingUser.email}</BodyRegular>
          </ConfigurationSection>
          <BirthdaySection userId={editingUser.userId}
                           birthdayMonth={editingUser.birthdayMonth}
                           birthdayDay={editingUser.birthdayDay} />
          <SectionSaveButton onSave={saveProfile} />
        </>}

        {activePanel === 'team' && <>
          <ConfigurationSection>
            <BodyLarge colour={Colours.blue} style={{marginBottom: 12}}>{t('settings-page.edit-user.teams-section')}</BodyLarge>
            <ChangeUsersTeamDropDownComponent editingUser={editingUser} teams={teams} />
            {departments.length > 0 && <ChangeUsersDepartmentDropDownComponent editingUser={editingUser} departments={departments} />}
          </ConfigurationSection>
          <ConfigurationSection>
            <BodyLarge colour={Colours.blue} style={{marginBottom: 12}}>{t('settings-page.edit-user.groups-section')}</BodyLarge>
            <UserWorkGroupsSection editingUser={editingUser} />
          </ConfigurationSection>
          <SectionSaveButton />
        </>}

        {activePanel === 'line-reports' && <>
          {panelTitle('settings.line-reports')}
          <UsersLineReports user={editingUser} />
          <SectionSaveButton />
        </>}

        {activePanel === 'office' && <>
          <SelectLocationSection disabled={!canChangeUsersDefaultOffice(currentUser)} user={editingUser} onLocationChange={onLocationChange} />
          {company?.automaticOfficeCheckinEnabled && <UserMacAddressSection user={editingUser} />}
          <SectionSaveButton />
        </>}

        {activePanel === 'access' && <>
          {panelTitle('settings-page.edit-user.access-tab')}
          <UserAccessPermittedControl userInfo={editingUser} />
          <UserPermissionSelection disabled={!canChangeUsersRole(currentUser)}
                                   permission={permission}
                                   userId={editingUser?.userId}
                                   role={editingUser?.role}
                                   onPermissionChanged={setPermission}/>
          <SectionSaveButton />
        </>}

        {activePanel === 'leave' && <>
          {panelTitle('settings-page.edit-user.leave-tab')}
          <UserHolidayAllowanceSection user={editingUser} />
        </>}

        {activePanel === 'responsibilities' && <>
          {panelTitle('settings-page.edit-user.responsibilities-tab')}
          <AdditionalResponsibilitiesSection user={editingUser} />
          <SectionSaveButton />
        </>}

        {activePanel === 'danger-zone' && <>
          <ConfigurationSection>
            <BodyLarge colour={Colours.blue} style={{marginBottom: 8}}>{t('settings-page.edit-user.delete-title')}</BodyLarge>
            <BodyRegular style={{marginBottom: 12}}>
              <Trans i18nKey="settings-page.edit-user.delete-description"
                     components={[<Link to="/manage/deleted-users" style={{color: 'inherit', textDecoration: 'underline'}} />]} />
            </BodyRegular>
            <BodyRegular style={{marginBottom: 24}}>
              <Trans i18nKey="settings-page.edit-user.delete-alternative"
                     components={[<InlineLinkButton type="button" onClick={() => setActivePanel('access')} />]} />
            </BodyRegular>
            {!isCompanyAdminUser(currentUser) &&
              <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 16}}>{t('settings-page.edit-user.delete-admin-only')}</BodyRegular>}
            <IconButton dataTest="editUserPage-delete-button"
                        disabled={!isCompanyAdminUser(currentUser)}
                        icon={IconTypes.Bin}
                        text="button.delete-user"
                        color="darkGrey"
                        onClick={openDeleteUserDialog} />
          </ConfigurationSection>
        </>}

      </SettingsSidebarLayout>
    </SettingsPageLayout>
  );
}

function UserPermissionSelection(props: { permission: Permission, userId: string, role: any, onPermissionChanged: any, disabled?: boolean }) {
  const current = useSelector(selectCurrentUser);
  const {t} = useTranslation();
  const isCompanyAdmin = current?.role === Permission.CompanyAdmin;
  const isDepartmentAdmin = current?.role === Permission.DepartmentAdmin;
  const isOfficeAdmin = current?.role === Permission.OfficeAdmin;
  const isTeamAdmin = current?.role === Permission.TeamAdmin;
  const [loading, setLoading] = useState(false);

  const setPermission = async (permission: Permission) => {
    if (!props.disabled) {
      try {
        setLoading(true);
        await updatePermissions(props.userId, permission);
        props.onPermissionChanged(permission)
      } finally {
        setLoading(false);
      }
    }
  }

  if (!props.role) {
    return null;
  }

  return (
    <Section>
      <BodyLarge colour={Colours.blue}>{t('settings-page.edit-user.permissions')}</BodyLarge>
      <div className="editUse__permissions">
        <div>
          <SelectableContentCard disabled={props.disabled || loading}
                                 selected={props.permission === Permission.Basic}
                                 title="settings-page.edit-user.standard-user"
                                 onClick={() => setPermission(Permission.Basic)}>
            <ul>
              <li><BodySmall>{t('settings-page.edit-user.basic-permissions')}</BodySmall></li>
            </ul>
          </SelectableContentCard>

          {(isCompanyAdmin || isTeamAdmin || isOfficeAdmin || isDepartmentAdmin) ?
            <SelectableContentCard disabled={props.disabled || loading}
                                   selected={props.permission === Permission.TeamAdmin}
                                   title="settings-page.edit-user.team-admin"
                                   onClick={() => setPermission(Permission.TeamAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.team-admin-permissions-1')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.team-admin-permissions-2')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.team-admin-permissions-3')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

          {isCompanyAdmin ?
            <SelectableContentCard disabled={props.disabled || loading} selected={props.permission === Permission.OfficeAdmin}
                                   title="settings-page.edit-user.office-admin" onClick={() => setPermission(Permission.OfficeAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-1')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-2')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-3')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-4')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

          {isCompanyAdmin ?
            <SelectableContentCard disabled={props.disabled || loading}
                                   selected={props.permission === Permission.DepartmentAdmin}
                                   title="settings-page.edit-user.department-admin"
                                   onClick={() => setPermission(Permission.DepartmentAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.department-admin-permissions-1')}</BodySmall></li>
                  <li><BodySmall>{t('settings-page.edit-user.department-admin-permissions-2')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

          {isCompanyAdmin ?
            <SelectableContentCard disabled={props.disabled || loading} selected={props.permission === Permission.CompanyAdmin}
                                   title="settings-page.edit-user.company-admin" onClick={() => setPermission(Permission.CompanyAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.company-admin-permissions-1')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.company-admin-permissions-2')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.company-admin-permissions-3')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

        </div>
      </div>
    </Section>
  )
}

// Reads as a text link inside a sentence but switches panel instead of navigating.
const InlineLinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

const RibbonSubtitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 12px;
`;
